<script setup>
import { useAxios } from '@/hooks/useAxios';
import { computed, onMounted, ref, watch } from 'vue';

const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    button: {
        type: String,
        default: ''
    },
    upper: {
        type: Boolean,
        default: false
    },
    maxlength: {
        type: Number,
        default: 4000
    },
    validationCheck: {
        type: Function,
        default: () => ({ isValid: true, message: '' })
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    labelEmpty: {
        type: Boolean,
        default: false
    },
    requestBody: {
        type: Object,
        default: () => ({})
    },
    justifyContent: {
        type: String,
        default: 'center'
    },
    width: {
        type: String,
        default: '9rem'
    },
    height: {
        type: String,
        default: '2.7rem'
    }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'change', 'preview-result']);
const previewData = ref(null);
const isReadonly = computed(() => props.readonly);
const isVisible = computed(() => props.visible);
const inputValue = ref(props.modelValue);
const ValidationMsg = ref('');
const errMessage = ref(props.errmsg);
const panels = ref([]);
const componentValues = ref({});
const requestBody = computed(() => props.requestBody);

const getRentalPreview = async (data) => {
    const config = {
        method: 'post'
    };

    try {
        const { execute, setHeader } = useAxios('/api/contract/normal/preview', config, {
            immediate: false,
            onSuccess(response) {
                const result = response.data.data?.[0];
                console.log('견적조회 결과', result.total_amount);

                emit('preview-result', {
                    total_amount: result.total_amount ?? '',
                    text: result.text ?? ''
                });

                previewData.value = result;
            },
            onError(err) {
                console.log('[ERROR]: ', err.errorMessage);
                previewData.value = {};

                emit('preview-result', {
                    total_amount: '',
                    text: err.errorMessage ?? ''
                });
            }
        });
        // setHeader('Content-Type', 'application/json');
        await execute(data);
    } catch (err) {
        console.log(err);
        previewData.value = {};
    }
};

const handleClickButton = (event) => {
    emit('click');
    const plainData = Object.fromEntries(Object.entries(props.requestBody).map(([key, value]) => [key, value]));

    getRentalPreview(plainData);
};

watch(
    () => props.modelValue,
    (newVal) => {
        if (props.upper) {
            newVal = newVal.toUpperCase();
        }

        inputValue.value = newVal || '';
    },
    { immediate: true }
);

// watch(
//     () => props.errmsg,
//     (newVal) => {
//         ValidationMsg.value = newVal || '';
//         errMessage.value = newVal || '';
//     },
//     { immediate: true, deep: true }
// );

onMounted(() => {
    console.log('[MOUNT] visible:', props.visible);
    console.log('[MOUNT] readonly:', props.readonly);
});
</script>

<template>
    <div
        v-if="!isReadonly && isVisible"
        class="flex flex-col"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label v-if="!labelEmpty" class="label">
            {{ label }}
            <!-- <span v-if="required" class="required-asterisk">*</span> -->
            <!-- <span v-if="errMessage" class="error-message">{{ errMessage }}</span> -->
        </label>

        <div class="relative flex items-center w-full h-12" :style="{ justifyContent: justifyContent || 'center' }">
            <button class="button-style" @click="handleClickButton" :style="{ width: width, height: height }">요금 미리보기</button>
        </div>
    </div>
</template>

<style scoped>
.input-wrapper {
    display: flex;
    flex-direction: column;
}

.label {
    font-family: 'SCoreDream600';
    display: flex;
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.input-font {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

input:focus {
    border-color: #0038ea;
    outline: none;
}

.text-right {
    text-align: right;
    padding-right: 6rem;
}

.error-border {
    border: 1px solid red !important;
}
.custom-input.p-inputtext {
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
}

.custom-input.p-inputtext:focus {
    border-color: #0038ea !important;
}

.button-style_b {
    font-family: 'SCoreDream500';
    font-size: 0.85rem;
    transition: color 0.3s ease;
    border-radius: 0.5rem;
    background-color: #f1f1f1;
    color: #4b5563;
    align-items: center;
    position: absolute;
    right: 0.5rem;
    width: 5.5rem;
    height: 1.8rem;
}
.button-style {
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    transition: color 0.3s ease;
    border-radius: 0.4rem;
    background-color: #f0f0ff;
    color: #3868ff;
    align-items: center;
    /* position: absolute; */
    display: flex;
    justify-content: center;
    border: 1px solid #3868ff;
}

.button-style:hover {
    background-color: #dddee0;
}

.button-style:focus {
    outline: none;
}

.error-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}
</style>
